import { useState, useCallback, useEffect } from 'react';
import {
    Card, Table, Stack, Paper, Button, Popover,
    Pagination, TableRow, MenuItem, TableBody, TableCell,
    Typography, IconButton, TableContainer, CircularProgress,
    TableHead
} from '@mui/material';
import Iconify from '../../iconify';
import CustomModal from '../../modal/CustomModal';
import { FormCreateEvent } from '../../forms/appMovilForms/createEvents';
import { useAxios } from '../../../context/AxiosHookContext';
import { useNotification } from '../../../context/NotificationContext';
import { formatoFechaAmFm } from '../../../context/DateInstance';

const TABLE_HEAD = [
    { label: 'Título' },
    { label: 'Tipo' },
    { label: 'Inicio' },
    { label: 'Fin' },
    { label: '' },
];

function EventosTabs() {
    const axios = useAxios();
    const { showSnackbar } = useNotification();

    const [eventsList, setEventsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [openMenu, setOpenMenu] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            setEventsList([]);
            const { data } = await axios.get(`/app-events?page=${page}&limit=10`);
            setEventsList(data.data.list || []);
            setTotalPages(data.data.total_pages || 1);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [axios, page]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleOpenMenu = (event, row) => {
        setOpenMenu(event.currentTarget);
        setSelectedEvent(row);
    };

    const handleCloseMenu = () => {
        setOpenMenu(null);
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await axios.delete(`/app-events/${selectedEvent._id}`);
            showSnackbar({ severity: 'success', title: 'Eventos', message: 'Evento eliminado exitosamente.' });
            handleCloseMenu();
            setPage(1);
            fetchEvents();
        } catch (error) {
            showSnackbar({ severity: 'error', title: 'Eventos', message: 'Error al eliminar el evento.' });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Eventos App Móvil</Typography>
                <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => { setSelectedEvent({}); setOpenModal(true); }}
                >
                    Nuevo evento
                </Button>
            </Stack>

            <Card>
                <TableContainer sx={{ minWidth: 700 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {TABLE_HEAD.map((col, i) => (
                                    <TableCell key={i}>{col.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {eventsList.map((row) => (
                                <TableRow hover key={row._id} tabIndex={-1}>
                                    <TableCell>
                                        <Typography variant="subtitle2" noWrap sx={{ maxWidth: 300 }}>
                                            {row.reason_title}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{row.eventType}</TableCell>
                                    <TableCell>{row.start_date ? formatoFechaAmFm(row.start_date) : '—'}</TableCell>
                                    <TableCell>{row.end_date ? formatoFechaAmFm(row.end_date) : '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, row)}>
                                            <Iconify icon="eva:more-vertical-fill" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>

                        {loading && (
                            <TableBody>
                                <TableRow>
                                    <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                                        <Paper sx={{ textAlign: 'center' }}>
                                            <CircularProgress size="30px" sx={{ color: '#aa9e97' }} />
                                        </Paper>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}

                        {eventsList.length === 0 && !loading && (
                            <TableBody>
                                <TableRow>
                                    <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                                        <Paper sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" paragraph>Sin resultados</Typography>
                                            <Typography variant="body2">No hay eventos registrados.</Typography>
                                        </Paper>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>

                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    sx={{ float: 'right', margin: 2 }}
                />
            </Card>

            <Popover
                open={Boolean(openMenu)}
                anchorEl={openMenu}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1, width: 140, '& .MuiMenuItem-root': { px: 1, typography: 'body2', borderRadius: 0.75 } } }}
            >
                <MenuItem onClick={() => { setOpenModal(true); handleCloseMenu(); }}>
                    <Iconify icon="eva:edit-fill" sx={{ mr: 2 }} />
                    Editar
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }} disabled={deleting}>
                    {deleting
                        ? <CircularProgress size="15px" sx={{ mr: 2 }} />
                        : <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
                    }
                    Eliminar
                </MenuItem>
            </Popover>

            <CustomModal
                open={openModal}
                modalStyle={{ maxWidth: 700, minWidth: 440 }}
                title={selectedEvent._id ? 'Editar evento' : 'Nuevo evento'}
            >
                <FormCreateEvent
                    onClose={() => { setOpenModal(false); setSelectedEvent({}); }}
                    onSuccess={() => { setOpenModal(false); setSelectedEvent({}); setPage(1); fetchEvents(); }}
                    valuesToEdit={selectedEvent}
                />
            </CustomModal>
        </>
    );
}

export default EventosTabs;
